import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import StoryListCard from '@/components/StoryListCard';
import { useHomeStories } from '@/hooks/useHomeStories';

// A hand-sized, finite set — not a feed. The landing page's philosophy
// section promises "no infinite scroll, no trending noise," and this section
// has to honour that: a fixed count, ending in a link out rather than a
// "load more". The shelf ends; the visitor chooses to go on.
//
// Which stories fill it is useHomeStories' decision, not this component's.
const HOME_STORY_COUNT = 6;

/**
 * Real stories on the landing page, directly under the hero.
 *
 * Before this existed, a mobile visitor's only routes to a story were typing
 * a search query or opening the hamburger menu — so the entire first visit
 * could pass without evidence that any story exists. Cards also teach the
 * search box: seeing how stories are framed is what tells someone what to
 * type.
 *
 * Placement is load-bearing. This must sit above the philosophy and
 * "How it works" sections; four screens down on a phone is invisible.
 *
 * If the fetch fails or there is nothing published, the section renders
 * nothing at all. A marketing page degrading to its previous layout is far
 * better than one showing an error card to a first-time visitor.
 */
export default function HomeStoriesSection({ tagOptions }) {
  const navigate = useNavigate();
  const { stories, total, loading, error } = useHomeStories(HOME_STORY_COUNT);

  if (error) return null;
  if (!loading && stories.length === 0) return null;

  return (
    <section className="py-12 md:py-16" data-testid="home-stories-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h3 className="text-3xl sm:text-4xl md:text-5xl font-serif tracking-tight leading-tight text-center text-[#2D2A26] mb-4 px-2">
          Start with one of these
        </h3>
        <p className="text-base md:text-lg leading-relaxed text-[#6B665E] text-center mb-10 max-w-2xl mx-auto">
          A few of the stories this library was built around. Read one, or search above for what you are going through.
        </p>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" aria-hidden>
            {Array.from({ length: 3 }, (_, i) => (
              <div key={i} className="bg-white rounded-xl border border-[#E6E2D6] p-6">
                <Skeleton className="h-4 w-24 mb-4" />
                <Skeleton className="h-7 w-3/4 mb-3" />
                <Skeleton className="h-4 w-40 mb-5" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-stories-grid">
              {stories.map((story) => (
                <StoryListCard
                  key={story.id}
                  story={story}
                  tagOptions={tagOptions}
                  onClick={() => navigate(`/story/${story.id}`)}
                />
              ))}
            </div>

            {total > stories.length && (
              <div className="text-center mt-10">
                <button
                  type="button"
                  onClick={() => navigate('/browse')}
                  className="text-[#C05621] hover:text-[#A0461A] font-medium hover:underline underline-offset-4"
                  data-testid="home-browse-all-btn"
                >
                  Browse all {total} stories →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
